import {
  Badge,
  Box,
  Button,
  Flex,
  Modal,
  ModalContent,
  ModalOverlay,
  Progress,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { useState } from "react";
import MappingTable from "./MappingTable";

export default function MappingModal({
  isOpen,
  onClose,
  breakdown,
  mapping,
  importFields,
  onMappingChange,
  missingRequired,
  unmappedRequiredFields = [],
  onBack,
  onConfirm,
  hasIssues,
  extraCsvFields,
  requiredFields,
  mappedRequiredCount,
  importId,
  setStep,
  startImport,
  selectedCase,
  selectedLinkType,
  outbreak,
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const headerBg = useColorModeValue("white", "gray.900");
  const footerBg = useColorModeValue("white", "gray.900");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textMuted = useColorModeValue("gray.500", "gray.400");

  const totalMapped = Object.values(mapping).filter(Boolean).length;
  const total = breakdown.length;

  const handleConfirmImport = async () => {
    const mergedBreakdown = breakdown.map((row) => ({
      ...row,
      matched_column: mapping[row.csv_header] || null,
    }));

    try {
      setIsSubmitting(true);
      const payload = {
        breakdown: mergedBreakdown,
        import_id: importId,
        ...(outbreak && {
          outbreak: {
            id: selectedCase?.data.id,
            PRODUCT: selectedCase?.data.PRODUCT,
            LINK_TYPE: selectedLinkType,
          },
        }),
      };
      const res = await fetch("/api/rosterProcessor", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      onConfirm(mergedBreakdown, result);
      onClose();
      setStep("PROCESSING");
    } catch (err) {
      console.error("Import failed:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      motionPreset="none"
      blockScrollOnMount={true}
      preserveScrollBarGap={false}
    >
      <ModalOverlay
        bg="blackAlpha.500"
        backdropFilter="blur(3px)"
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
      />

      <ModalContent
        position="fixed"
        top={{ base: 0, md: "20px" }}
        left={{ base: 0, md: "20px" }}
        right={{ base: 0, md: "20px" }}
        bottom={{ base: 0, md: "20px" }}
        m={0}
        maxW="none"
        w="auto"
        h="90vh"
        maxH="90vh"
        borderRadius={{ base: "none", md: "xl" }}
        overflow="hidden"
        display="flex"
        flexDirection="column"
        boxShadow="2xl"
        onWheel={(e) => e.stopPropagation()}
      >
        {/* ── Fixed header ── */}
        <Box
          bg={headerBg}
          borderBottom="1px solid"
          borderColor={borderColor}
          px={6}
          py={4}
          flexShrink={0}
        >
          <Flex align="flex-start" justify="space-between" gap={4}>
            {/* Left: title + badges */}
            <Box flex={1} minW={0}>
              <Flex align="center" gap={3} mb={1} flexWrap="wrap">
                <Text fontSize="15px" fontWeight="700" letterSpacing="-0.01em">
                  Field Mapping Review
                </Text>
                <Badge
                  colorScheme="blue"
                  borderRadius="full"
                  px={2.5}
                  py={0.5}
                  fontSize="11px"
                >
                  {totalMapped} / {total} mapped
                </Badge>

                {/* Required fields mapped badge */}
                <Badge
                  colorScheme={
                    mappedRequiredCount < requiredFields.length
                      ? "red"
                      : "green"
                  }
                  borderRadius="full"
                  px={2.5}
                  py={0.5}
                  fontSize="11px"
                >
                  {mappedRequiredCount} / {requiredFields.length} required
                </Badge>

                {/* Unmapped required fields — distinct warning badge */}
                {unmappedRequiredFields.length > 0 && (
                  <Badge
                    colorScheme="red"
                    variant="solid"
                    borderRadius="full"
                    px={2.5}
                    py={0.5}
                    fontSize="11px"
                  >
                    ⛔ {unmappedRequiredFields.length} required unmapped
                  </Badge>
                )}

                {missingRequired.length > 0 &&
                  unmappedRequiredFields.length === 0 && (
                    <Badge
                      colorScheme="orange"
                      borderRadius="full"
                      px={2.5}
                      py={0.5}
                      fontSize="11px"
                    >
                      {missingRequired.length} required fields unmapped
                    </Badge>
                  )}
              </Flex>

              <Text fontSize="12px" color={textMuted}>
                Each CSV column is shown as a column. Set "Maps To" to link it
                to an import field, or leave as "Not mapped" to skip it.
              </Text>

              {/* Unmapped required fields list under the description */}
              {unmappedRequiredFields.length > 0 && (
                <Flex gap={1.5} flexWrap="wrap" mt={2}>
                  {unmappedRequiredFields.map((f) => (
                    <Badge
                      key={f}
                      colorScheme="red"
                      variant="subtle"
                      borderRadius="full"
                      fontSize="10px"
                      px={2}
                      py={0.5}
                    >
                      {f}
                    </Badge>
                  ))}
                </Flex>
              )}
            </Box>

            {/* Center: required mapped meter or extra fields */}
            <Box flexShrink={0} maxW="300px">
              {hasIssues ? (
                <Box
                  bg="orange.50"
                  p={4}
                  borderRadius="lg"
                  maxH="120px"
                  overflowY="auto"
                >
                  <Text fontSize="xs" fontWeight="600" color="orange.600">
                    ⚠ Extra fields
                  </Text>
                  <Text fontSize="xs" color="orange.500" mt={0.5}>
                    {extraCsvFields.join(", ")}
                  </Text>
                </Box>
              ) : (
                <Box bg="gray.50" p={4} borderRadius="lg">
                  <Text fontSize="xs" color="gray.500">
                    Required mapped
                  </Text>
                  <Text
                    fontSize="2xl"
                    fontWeight="600"
                    color={
                      mappedRequiredCount < requiredFields.length
                        ? "red.500"
                        : "green.500"
                    }
                  >
                    {mappedRequiredCount} / {requiredFields.length}
                  </Text>
                  <Progress
                    value={
                      requiredFields.length
                        ? Math.round(
                            (mappedRequiredCount / requiredFields.length) * 100,
                          )
                        : 0
                    }
                    size="xs"
                    colorScheme={
                      mappedRequiredCount < requiredFields.length
                        ? "red"
                        : "green"
                    }
                    mt={2}
                  />
                </Box>
              )}
            </Box>

            {/* Close X */}
            <Button
              size="sm"
              onClick={onClose}
              minW="32px"
              h="32px"
              p={0}
              flexShrink={0}
              borderRadius="md"
              fontSize="16px"
              color={textMuted}
              _hover={{ bg: "gray.100", color: "gray.700" }}
            >
              ✕
            </Button>
          </Flex>
        </Box>

        {/* ── Table — fills all available space ── */}
        <Box flex="1" overflow="hidden" minH={0} px={5}>
          <MappingTable
            breakdown={breakdown}
            mapping={mapping}
            importFields={importFields}
            onMappingChange={onMappingChange}
            requiredFields={requiredFields}
            unmappedRequiredFields={unmappedRequiredFields}
          />
        </Box>

        {/* ── Fixed footer ── */}
        <Box
          bg={footerBg}
          borderTop="1px solid"
          borderColor={borderColor}
          px={6}
          py={4}
          flexShrink={0}
          mb={10}
        >
          <Flex align="center" justify="space-between">
            <Box></Box>

            <Flex align="center" gap={3}>
              {unmappedRequiredFields.length > 0 && (
                <Text fontSize="12px" color="red.500" fontWeight="600">
                  ⛔ {unmappedRequiredFields.length} required field
                  {unmappedRequiredFields.length > 1 ? "s" : ""} still unmapped
                  — import may fail
                </Text>
              )}
              {unmappedRequiredFields.length === 0 &&
                missingRequired.length > 0 && (
                  <Text fontSize="12px" color="orange.500" fontWeight="500">
                    {missingRequired.length} optional field
                    {missingRequired.length > 1 ? "s" : ""} not mapped — they
                    will be skipped
                  </Text>
                )}
              <Button
                size="sm"
                bg="teal.500"
                _disabled={{ bg: "teal.400" }}
                color="white"
                borderRadius="sm"
                fontWeight="500"
                px={5}
                onClick={handleConfirmImport}
                loadingText="Starting import..."
                isLoading={isSubmitting}
                isDisabled={isSubmitting}
              >
                Confirm &amp; start import
              </Button>
            </Flex>
          </Flex>
        </Box>
      </ModalContent>
    </Modal>
  );
}
